/**
 * MyActivityLog.tsx — the clinician's OWN audit trail on My stats (UX Wave 4
 * §3, 2026-07-21): "where's audit for clinicians".
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 4.
 *
 * WHY: admins have the global G2 audit tab; a clinician gets a SELF-SCOPED
 * view only. Both reads are sent with `actorUserId=self` explicitly — the
 * server enforces the scope (R41: the UI is never the control), the client
 * simply never asks for anyone else. MONEY-FREE by construction: the clinic
 * vocabulary carries no money action, and the orders read is filtered to the
 * money-free CLINICAL actions only (approve / reject / preapprove /
 * prescription) — `payment_refunded` / `compensation_updated` are never
 * requested, so no money-adjacent row can reach this surface (the
 * money-redaction guard scans the whole My-stats tab). Rows are ids / enums /
 * timestamps (+ the recorded reason) — PHI-free, R19.
 */
import { useCallback, useEffect, useState } from 'react';
import { getClinicAudit } from '../api/clinic';
import { getOrdersAudit } from '../api/orders';
import type { AuditEntry } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { formatUtcDate } from '../lib/format';
import { SectionTitle } from '../staff/ui';
import { Badge, Card, ErrorState, Help, Spinner } from '../ui/kit';

/** The orders actions a clinician performs that carry NO money meaning. */
const MONEY_FREE_ORDER_ACTIONS = ['order_approved', 'order_rejected', 'order_preapproved', 'prescription_issued'];

/** Rows per service — merged and re-capped below. */
const PAGE = 25;

/** Humanise an audit action/target enum ("order_approved" → "order approved"). */
function words(value: string): string {
  return value.replace(/_/g, ' ');
}

export function MyActivityLog() {
  const { me } = useAuth();
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const userId = me?.userId;

  const load = useCallback(() => {
    if (!userId) return;
    setStatus('loading');
    Promise.all([
      // Self-scoped: BOTH reads carry actorUserId=me; the orders read is also
      // narrowed to the money-free clinical actions (see the module doc).
      getClinicAudit({ actorUserId: userId, limit: PAGE }),
      getOrdersAudit({ actorUserId: userId, limit: PAGE, action: [...MONEY_FREE_ORDER_ACTIONS] }),
    ])
      .then(([clinic, orders]) => {
        const merged = [...clinic.items, ...orders.items]
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
          .slice(0, PAGE);
        setEntries(merged);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <SectionTitle>My activity log</SectionTitle>
      <Help>Your own recorded actions — callbacks, deals, handovers and clinical decisions.</Help>
      {status === 'loading' ? (
        <Spinner label="Loading your activity log…" />
      ) : status === 'error' || !entries ? (
        <ErrorState message="Sorry — we could not load your activity log." onRetry={load} />
      ) : entries.length === 0 ? (
        <Help style={{ marginBottom: 0 }}>No recorded activity yet — this fills in as you work.</Help>
      ) : (
        <ol aria-label="My audit entries" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }}>
          {entries.map((entry) => (
            <li
              key={entry.auditId}
              style={{ border: '1px solid var(--gps-border)', borderRadius: 'var(--gps-r-md)', padding: '8px 12px' }}
            >
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', fontSize: '13px' }}>
                <span>
                  <Badge tone="neutral">{words(entry.action)}</Badge>
                  <span style={{ marginLeft: '8px', color: 'var(--gps-muted)' }}>
                    {words(entry.targetType)} {entry.targetId}
                  </span>
                </span>
                <span style={{ color: 'var(--gps-muted)' }}>{formatUtcDate(entry.createdAt)}</span>
              </span>
              {entry.reason ? (
                <span style={{ display: 'block', marginTop: '4px', fontSize: '13px', color: 'var(--gps-text)' }}>
                  Reason: {entry.reason}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
